import { LegalDocumentDownload } from '@chiroup/core/types/LegalDocument.type';

const legalPdfUrl = async (
  document: LegalDocumentDownload,
  expectedSha256?: string
): Promise<string> => {
  const bytes = Uint8Array.from(atob(document.pdfBase64), char => char.charCodeAt(0));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const sha256 = Array.from(new Uint8Array(digest), byte =>
    byte.toString(16).padStart(2, '0')
  ).join('');
  if (sha256 !== document.sha256 || (expectedSha256 && sha256 !== expectedSha256)) {
    throw new Error('Document integrity could not be verified. Please try again.');
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  return url;
};

export const saveLegalPdf = async (document: LegalDocumentDownload): Promise<void> => {
  const anchor = window.document.createElement('a');
  anchor.href = await legalPdfUrl(document);
  anchor.download = document.filename;
  window.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

export const previewLegalPdf = async (
  load: () => Promise<LegalDocumentDownload>,
  expectedSha256: string
): Promise<void> => {
  // Reserve the tab during the click. Opening it after network/crypto awaits is
  // blocked by browsers that require transient user activation, including Safari.
  const preview = window.open('about:blank', '_blank');
  if (!preview) throw new Error('Allow pop-ups for ChiroUp, then open the document again.');
  preview.opener = null;
  try {
    const url = await legalPdfUrl(await load(), expectedSha256);
    if (preview.closed) {
      throw new Error('The document tab was closed. Open the document again to review it.');
    }
    preview.location.replace(url);
  } catch (error) {
    preview.close();
    throw error;
  }
};

export const legalDocumentError = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'response' in error) {
    const response = error.response;
    if (response && typeof response === 'object' && 'data' in response) {
      const data = response.data;
      if (
        data &&
        typeof data === 'object' &&
        'message' in data &&
        typeof data.message === 'string'
      ) {
        return data.message;
      }
    }
  }
  return 'The document request could not be completed. Please try again.';
};
